import { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import WelcomePanel from './WelcomePanel';
import './style.css';

const ChatBox = ({ onSendMessage, placeholder = '输入消息...' }) => {
  const [showWelcome, setShowWelcome] = useState(true);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef(null);  // 用于中断流式响应

  
  // 滚动相关引用和状态
  const messagesContainerRef = useRef(null);
  const isAutoScrollingRef = useRef(false);
  const userScrollTimeoutRef = useRef(null);
  const lastScrollHeightRef = useRef(0);


  // 智能滚动函数
  const smartScrollToBottom = useCallback((behavior = 'auto') => {
    if (!messagesContainerRef.current) return;
    
    const container = messagesContainerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceToBottom = scrollHeight - (scrollTop + clientHeight);
    
    // 如果距离底部较近(300px内)或者内容高度增加(新消息)
    if (distanceToBottom < 300 || scrollHeight > lastScrollHeightRef.current) {
      isAutoScrollingRef.current = true;
      
      container.scrollTo({
        top: scrollHeight,
        behavior
      });
      
      // 标记滚动结束
      const onScrollEnd = () => {
        if (Math.abs(container.scrollTop + container.clientHeight - container.scrollHeight) < 10) {
          isAutoScrollingRef.current = false;
          container.removeEventListener('scroll', onScrollEnd);
        }
      };
      
      container.addEventListener('scroll', onScrollEnd);
      setTimeout(() => {
        isAutoScrollingRef.current = false;
        container.removeEventListener('scroll', onScrollEnd);
      }, 500);
    }
    
    lastScrollHeightRef.current = scrollHeight;
  }, []);

  // 滚动事件处理
  const handleScroll = useCallback(() => {
    // 如果是系统自动滚动，不处理
    if (isAutoScrollingRef.current) return;
    
    // 清除之前的超时
    if (userScrollTimeoutRef.current) {
      clearTimeout(userScrollTimeoutRef.current);
    }
    
    // 设置新的超时，1.5秒后认为用户停止滚动
    userScrollTimeoutRef.current = setTimeout(() => {
      // 检查是否需要恢复自动滚动
      const container = messagesContainerRef.current;
      if (!container) return;
      
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceToBottom = scrollHeight - (scrollTop + clientHeight);
      
      // 如果接近底部(200px内)，恢复自动滚动
      if (distanceToBottom < 200) {
        smartScrollToBottom('smooth');
      }
    }, 1500);
  }, [smartScrollToBottom]);

  const handleStopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort("用户主动停止"); // 明确中止原因
      abortControllerRef.current = null;
      
      // 立即更新状态
      setMessages(prev => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage.status === 'streaming') {
          return [
            ...prev.slice(0, -1),
            {
              ...lastMessage,
              status: 'interrupted', // 添加新的状态表示被中断
              content: lastMessage.content + '\n'
            }
          ];
        }
        return prev;
      });
      
      setIsLoading(false);
    }
  }, []);

  // 发送消息处理
  const handleSend = useCallback(async () => {
    if (!inputText.trim()) return;

    if (isLoading) {
      handleStopStreaming();
      return;
    }

    // 添加用户消息
    const userMessage = {
      id: Date.now().toString(),
      content: inputText.trim(),
      role: 'user',
      status: 'complete'
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);
    let error = null;

    try {
      // 添加AI临时消息
      const tempAiId = `temp-${Date.now()}`;
      setMessages(prev => [...prev, {
        id: tempAiId,
        content: '',
        role: 'assistant',
        status: 'streaming'
      }]);

      // 立即滚动到底部
      smartScrollToBottom('smooth');

      // 调用API
      abortControllerRef.current = new AbortController();
      const stream = await onSendMessage(inputText.trim(), abortControllerRef.current.signal);
      if (!stream) return;

      // 处理流式数据
      const reader = stream.getReader();
      let done = false;

      while (!done) {
        const { value, done: streamDone } = await reader.read();
        done = streamDone;
        if (value) {
          const chunk = new TextDecoder().decode(value);
          setMessages(prev => prev.map(msg => 
            msg.id === tempAiId 
              ? { ...msg, content: msg.content + chunk } 
              : msg
          ));
          
          // 流式更新时智能滚动
          smartScrollToBottom('auto');
        }
      }

      // 标记为完成
      setMessages(prev => prev.map(msg =>
        msg.id === tempAiId ? { ...msg, status: 'complete' } : msg
      ));
    } catch (err) {
      error = err; 
      if (!error) {  // 添加null检查
        console.error('发生未知错误');
        setMessages(prev => prev.map(msg =>
          msg.id === prev[prev.length - 1].id 
            ? { ...msg, content: '请求发生未知错误', status: 'error' }
            : msg
        ));
        return;
      } else if (error.name === 'AbortError' || error === '用户主动停止') {
        console.log('用户中止了请求');
        return; // 直接返回，不显示错误
      }
      console.error('请求错误:', error);
      setMessages(prev => prev.map(msg =>
        msg.id === prev[prev.length - 1].id 
          ? { ...msg, content: '请求失败: ' + error.message, status: 'error' }
          : msg
      ));
    } finally {
      abortControllerRef.current = null;
      setIsLoading(false);
      smartScrollToBottom('smooth');  
    }
  }, [inputText, isLoading, onSendMessage, smartScrollToBottom, handleStopStreaming]);


  const handleStartChatWithMessage = useCallback(async (message) => {

    // 先中止任何现有请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // 初始化新的中止控制器
    abortControllerRef.current = new AbortController();

    // 1. 先设置加载状态
    setIsLoading(true);
    
    // 2. 同步更新状态（确保立即渲染）
    setShowWelcome(false);
    
    const userMsgId = `user-${Date.now()}`;
    const aiMsgId = `ai-${Date.now()}`;

    // 3. 使用requestAnimationFrame确保DOM更新
    requestAnimationFrame(() => {
      setMessages([{
        id: userMsgId,
        content: message,
        role: 'user',
        status: 'complete'
      }, {
        id: aiMsgId,
        content: '', // 硬编码确保显示
        role: 'assistant',
        status: 'streaming'
      }]);
      
      // 4. 强制移动端布局重绘
      setTimeout(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.style.display = 'none';
          void container.offsetHeight; // 触发重绘
          container.style.display = '';
        }
      }, 50);
    });

    try {
      // 直接处理流式响应，不需要存储stream变量
      const reader = (await onSendMessage(message,  abortControllerRef.current.signal)).getReader();
      let fullContent = '';
      let done = false;

      // 在循环开始前声明更新函数
      const updateMessage = (content) => {
        setMessages(prev => prev.map(msg => 
          msg.id === aiMsgId
            ? { ...msg, content }
            : msg
        ));
      };

      while (!done) {
        try {
          const { value, done: streamDone } = await reader.read();
          done = streamDone;
          if (value) {
            const chunk = new TextDecoder().decode(value);
            fullContent += chunk;
            updateMessage(fullContent); // 使用独立的更新函数
            // 流式更新时智能滚动
            smartScrollToBottom('auto');
          }
        } catch (error) {
          if (error.name === 'AbortError' || error === '用户主动停止') {
            console.log('Stream aborted by user');
            break;
          }
          throw error;
        }
      }

      // 7. 标记完成
      setMessages(prev => prev.map(msg =>
        msg.id === aiMsgId
          ? { ...msg, status: 'complete' }
          : msg
      ));

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error:', error);
        setMessages(prev => prev.map(msg =>
          msg.id === prev[prev.length - 1].id 
            ? { ...msg, content: '请求失败，请重试', status: 'complete' } 
            : msg
        ));
      }
    } finally {
      abortControllerRef.current = null;
      setIsLoading(false);
      smartScrollToBottom('smooth');  
    }
  }, [onSendMessage, smartScrollToBottom]);

  // 设置滚动监听
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  // 初始滚动到底部
  useEffect(() => {
    if (messages.length > 0) {
      smartScrollToBottom('auto');
    }
  }, [messages.length, smartScrollToBottom]);

  if (showWelcome) {
    return (
      <div className="chat-grid-container">
        <WelcomePanel onStartChat={handleStartChatWithMessage}
        onSendMessage={(msg) => handleSend(msg)} />
      </div>
    );
  }

  return (
    <div className="chat-grid-container">
      <div className="messages-grid" ref={messagesContainerRef}>
        <div className="messages-content">
          {messages.map((message) => (
            <div key={message.id} className={`message-grid ${message.role}`}>
              {message.role === 'assistant' && (
                <div className="assistant-header">
                  <div className="assistant-logo">
                    <img src="./评logo_50x50.ico" alt="AI" width={24} height={24} />
                  </div>
                </div>
              )}
              <div className="message-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {message.content}
                </ReactMarkdown>
                {message.status === 'streaming' && message.content === '' && (
                  <span className="loading-dots"></span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="input-grid">
        <div className="input-wrapper">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
                e.preventDefault();
                handleSend();
              }
            }}
            // disabled={isLoading}
            placeholder={placeholder}
            rows={3}
            className="chat-input"
          />
          <button
            onClick={() => {
              if (isLoading) {
                handleStopStreaming();
              } else {
                handleSend();
              }
            }}
            disabled={!inputText.trim() && !isLoading}
            className={`send-button-inside ${isLoading ? 'stop-button' : ''}`}
          >
          {isLoading ? (
            <div className="stop-icon-square"></div> 
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          </button>
        </div>
      </div>

      <div className="footer-grid">
        内容由 AI 生成，请仔细甄别
      </div>
    </div>
  );
};

export default ChatBox;